<template>
  <section class="hero-scene" aria-label="PS78 hero">
    <div class="halo-wrap halo-left" ref="haloLeftWrap">
      <div class="halo halo-left-inner"></div>
    </div>
    <div class="halo-wrap halo-right" ref="haloRightWrap">
      <div class="halo halo-right-inner"></div>
    </div>
    <div class="noise"></div>
    <canvas class="star-rain" ref="starCanvas" aria-hidden="true"></canvas>
    <div class="hero-copy" ref="heroCopy" :style="heroStyle">
      <div class="logo">
        <span class="logo-main">PS78</span>
        <span class="logo-glow">PS78</span>
      </div>
      <div class="hero-copy-text" aria-live="polite">
        <p class="hero-default" :class="{ 'is-hidden': hoveredProjectIndex !== null }">{{ heroDefaultText }}</p>
        <p class="hero-hover" v-if="hoveredProjectIndex !== null" :key="hoveredProjectIndex + '-' + _descIdx" :class="{ pop: isTextPopping, 'is-wrap': shouldWrapHeroText }">
          <span v-for="(w, idx) in hoverWords" :key="idx" :class="{ 'is-lit': idx === currentWordIndex }">{{ w }}</span>
        </p>
      </div>
    </div>
      <div class="project-orbit" ref="orbit" aria-hidden="false">
        <div v-for="(p, i) in projects" :key="p.name" class="orbit-item" :class="{ 'is-active': hoveredProjectIndex === i }" :style="projectPositions[i] ? projectPositions[i].style : {}" @click="openProject(p.link)" @pointerenter="onProjectEnter(i)" @pointerleave="onProjectLeave(i)" role="button" :aria-label="p.name">
          <div class="orbit-thumb" :style="projectPositions[i] ? projectPositions[i].thumbStyle : {}">
            <img class="lazy-img" :data-src="p.thumbnail" :alt="p.name" />
          </div>
          <div class="orbit-label" :style="projectPositions[i] ? projectPositions[i].labelStyle : {}" aria-hidden="true">
            <svg class="orbit-label-svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
              <g class="orbit-label-chars">
                <text v-for="(c, ci) in (projectPositions[i] ? projectPositions[i].labelChars : [])" :key="ci" class="orbit-label-char" :x="c.x" :y="c.y" :transform="`rotate(${c.rotate} ${c.x} ${c.y})`" text-anchor="middle" dominant-baseline="central">{{ c.char }}</text>
              </g>
            </svg>
          </div>
        </div>
      </div>
  </section>
</template>

<script>
import projectsData from '../data/projects.json'
export default {
  name: 'HeroScene',
  data() {
    return {
      rotX: 0,
      rotY: 0,
      targetX: 0,
      targetY: 0,
      rafId: null,
      mouseX: window.innerWidth / 2,
      mouseY: window.innerHeight / 2,
      halos: []
      ,
      // star rain
      stars: [],
      starCount: 50,
      starCtx: null,
      _lastStarT: null,
      // project orbit
      projects: projectsData,
      projectPositions: [],
      spinRate: (Math.PI * 2) / 45, // full rotation in ~45s
      spinAngle: 0,
      rotationDuration: 45,
      // spin control (smooth pause/restart)
      currentSpinRate: (Math.PI * 2) / 45,
      targetSpinRate: (Math.PI * 2) / 45,
      spinEase: 0.06,
      _lastOrbitT: null,
      // hover state for project text
      hoveredProjectIndex: null,
      // lazy-loading observer
      lazyObserver: null,
      // description cycle & particles
      heroDefaultText: 'Welcome to my space.',
      currentHoverText: '',
      _descTimer: null,
      _descIdx: 0,
      isTextPopping: false,
      currentWordIndex: 0,
      _wordTimer: null,
      // minimum time per word when distributing duration (ms)
      minWordMs: 120,
      // per-character spacing multiplier (1.0 = normal, >1 increases spacing). Adjust this to change letter spacing for circular labels.
      labelSpacingFactor: 1.05,
      // label rotation (radians) and rotation speed (radians/sec). This makes circular label text orbit the thumbnail center.
      labelRotation: 0,
      // negative value makes rotation go the opposite way
      labelSpinRate: -(Math.PI * 2) / 28, // full rotation in ~28s (counter-clockwise)
      _particleTimeouts: [],
      // observer for measuring default hero text width
      _heroDefaultObserver: null
    }
  },
  computed: {
    heroStyle() {
      return {
        transform: `perspective(800px) rotateX(${this.rotX.toFixed(3)}deg) rotateY(${this.rotY.toFixed(3)}deg)`,
        willChange: 'transform'
      }
    },
    shouldWrapHeroText() {
      // wrap only when the hovered text is longer (characters) than the default hero text
      return !!(this.currentHoverText && this.currentHoverText.length > (this.heroDefaultText || '').length)
    },
    hoverWords() {
      if (!this.currentHoverText) return []
      return String(this.currentHoverText).trim().split(/\s+/)
    }
  },
  mounted() {
    this.onMouseMove = this.handleMouseMove.bind(this)
    window.addEventListener('mousemove', this.onMouseMove)
    this.$nextTick(() => {
      this.initHalos()
      this.initStars()
      this.initOrbit()
      this.rafId = requestAnimationFrame((t) => this.animate(t))
    })
  },
  beforeDestroy() {
    window.removeEventListener('mousemove', this.onMouseMove)
    if (this.onResizeStars) window.removeEventListener('resize', this.onResizeStars)
    if (this.onResizeOrbit) window.removeEventListener('resize', this.onResizeOrbit)
    if (this.lazyObserver) this.lazyObserver.disconnect()
    if (this._particleTimeouts && this._particleTimeouts.length) {
      this._particleTimeouts.forEach(t => clearTimeout(t))
      this._particleTimeouts = []
    }
    if (this.rafId) cancelAnimationFrame(this.rafId)
  },
  beforeUnmount() {
    window.removeEventListener('mousemove', this.onMouseMove)
    if (this.onResizeStars) window.removeEventListener('resize', this.onResizeStars)
    if (this.onResizeOrbit) window.removeEventListener('resize', this.onResizeOrbit)
    if (this.lazyObserver) this.lazyObserver.disconnect()
    if (this._heroDefaultObserver) { this._heroDefaultObserver.disconnect(); this._heroDefaultObserver = null }
    if (this._particleTimeouts && this._particleTimeouts.length) {
      this._particleTimeouts.forEach(t => clearTimeout(t))
      this._particleTimeouts = []
    }
    if (this.rafId) cancelAnimationFrame(this.rafId)
  },
  methods: {
    handleMouseMove(e) {
      // update globals for halos
      this.mouseX = e.clientX
      this.mouseY = e.clientY

      const el = this.$refs.heroCopy
      if (!el) return
      const rect = el.getBoundingClientRect()
      const cx = rect.left + rect.width / 2
      const cy = rect.top + rect.height / 2
      const dx = e.clientX - cx
      const dy = e.clientY - cy
      const maxDeg = 60
      // Normalize by viewport diagonal so movement isn't constrained to element box
      const maxDist = Math.hypot(window.innerWidth, window.innerHeight) / 2
      const nx = dx / maxDist
      const ny = dy / maxDist
      const clamp = (v) => Math.max(-1, Math.min(1, v))
      this.targetY = clamp(nx) * maxDeg
      this.targetX = clamp(-ny) * maxDeg
    },
    initHalos() {
      const leftWrap = this.$refs.haloLeftWrap
      const rightWrap = this.$refs.haloRightWrap
      const list = []
      ;[leftWrap, rightWrap].forEach((wrap) => {
        if (!wrap) return
        const r = wrap.getBoundingClientRect()
        const cx = r.left + r.width / 2
        const cy = r.top + r.height / 2
        const maxSize = Math.random() * 2.0 // scale factor up to 2.0
        list.push({
          el: wrap,
          x: 0,
          y: 0,
          targetX: 0,
          targetY: 0,
          anchorX: 0,
          anchorY: 0,
          phase: Math.random() * Math.PI * 2,
          idleAmp: 12 + Math.random() * 8,
          avoidRadius: Math.max(window.innerWidth, window.innerHeight) * 0.25,
          avoidStrength: 80 + Math.random() * 40,
          baseRect: r,
          baseCenter: { cx, cy },
          size: 0,
          targetSize: maxSize,
          state: 'growing', // 'growing', 'shrinking'
          cycleStartTime: (performance.now() || Date.now()) / 1000,
          cycleDuration: 30 // seconds for growth/shrink
        })
        wrap.style.setProperty('--tx', '0px')
        wrap.style.setProperty('--ty', '0px')
        wrap.style.setProperty('--scale', '0')
      })
      this.halos = list
      // attach hover handlers so each halo jumps to a new anchor when hovered
      this.halos.forEach((h, idx) => {
        const handler = (ev) => this.onHaloEnter(idx, ev)
        h._enterHandler = handler
        h.el.addEventListener('pointerenter', handler)
      })
    },

    /* Star rain system (canvas) */
    initStars() {
      const canvas = this.$refs.starCanvas
      if (!canvas) return
      this.starCtx = canvas.getContext('2d')
      this.onResizeStars = this.resizeStars.bind(this)
      window.addEventListener('resize', this.onResizeStars)
      this.resizeStars()
      this.stars = []
      for (let i = 0; i < this.starCount; i++) {
        this.stars.push(this.spawnStar(true))
      }
      this._lastStarT = (performance.now() || Date.now()) / 1000
    },

    /* Project orbit: position project thumbnails around hero and animate spin */
    initOrbit() {
      // ensure positions exist
      this.updateProjectPositions()
      // spin defaults
      this.currentSpinRate = this.spinRate
      this.targetSpinRate = this.spinRate
      this.spinEase = 0.06
      this._lastOrbitT = null
      this.onResizeOrbit = this.handleResize.bind(this)
      window.addEventListener('resize', this.onResizeOrbit)
      // lazy load thumbnails
      this.initLazyLoad()
      // measure default hero text width and watch for changes
      this.updateHeroDefaultWidth()
      if (typeof ResizeObserver !== 'undefined') {
        this._heroDefaultObserver = new ResizeObserver(() => this.updateHeroDefaultWidth())
        const el = this.$el ? this.$el.querySelector('.hero-copy-text .hero-default') : null
        if (el) this._heroDefaultObserver.observe(el)
      }
    },

    updateHeroDefaultWidth() {
      // measure the rendered width of the default text and write to CSS var
      this.$nextTick(() => {
        const el = this.$el ? this.$el.querySelector('.hero-copy-text .hero-default') : null
        const target = this.$refs.heroCopy
        if (el && target) {
          const w = Math.ceil(el.getBoundingClientRect().width)
          target.style.setProperty('--hero-default-width', w + 'px')
        }
      })
    },

    handleResize() {
      this.updateProjectPositions()
    },

    updateProjectPositions() {
      if (!this.projects || !this.projects.length) return
      const heroEl = this.$refs.heroCopy
      const hr = heroEl ? heroEl.getBoundingClientRect() : { left: window.innerWidth/2, top: window.innerHeight/2, width:0, height:0 }
      const cx = hr.left + hr.width / 2
      const cy = hr.top + hr.height / 2
      const n = this.projects.length
      const minDim = Math.min(window.innerWidth, window.innerHeight)
      const radius = Math.max(120, Math.round(minDim * 0.28 + Math.max(hr.width, hr.height) * 0.6))
      this.projectPositions = this.projects.map((p, i) => {
        const base = (Math.PI * 2 * i) / n
        const angle = base + this.spinAngle
        const x = Math.cos(angle) * radius
        const y = Math.sin(angle) * radius
        const left = Math.round(cx + x)
        const top = Math.round(cy + y)
        const z = Math.round(1000 - top)
        // approximate thumbnail diameter from CSS clamp(48px, 6.2vw, 72px)
        const vw = window.innerWidth
        const thumbDia = Math.min(Math.max(48, Math.round(vw * 0.062)), 72)
        const labelMargin = 8 // base px gap between thumb edge and label circle
        const extraGap = 16 // additional spacing to make letters sit further out (increased to avoid clipping)
        const strokePad = 6 // extra padding for text stroke/overflow
        // determine thumb scale (active hover state) so label spacing keeps up with thumbnail size
        const scale = (this.hoveredProjectIndex === i) ? 2 : 1
        const thumbDiaScaled = Math.round(thumbDia * scale)
        // square bounding box for svg — increase to accommodate extra gap, stroke and thumbnail scale
        const labelSize = Math.round(thumbDiaScaled + (labelMargin + extraGap) * 2 + strokePad * 2)
        // Keep thumbnails upright (no self-rotation) while they orbit
        return {
          style: {
            position: 'absolute',
            left: `${left}px`,
            top: `${top}px`,
            transform: 'translate(-50%,-50%)',
            zIndex: z
          },
          thumbStyle: {
            '--r': `0deg`
          },
          labelStyle: {
            width: `${labelSize}px`,
            height: `${labelSize}px`,
            left: '50%',
            top: '50%',
            transform: 'translate(-50%,-50%)',
            pointerEvents: 'none',
            transition: 'width 260ms ease, height 260ms ease, transform 260ms ease'
          },
          // per-character positions for circular text (SVG coords 0..100)
          labelChars: this.computeLabelChars(p.name, thumbDiaScaled, labelSize)
        }
      });
    },

    openProject(link) {
      if (!link) return
      window.open(link, '_blank', 'noopener')
    },

    onProjectEnter(i) {
      // smooth brake
      this.targetSpinRate = 0
      this.hoveredProjectIndex = i
      // recompute positions so label spacing updates for the scaled thumbnail
      this.updateProjectPositions()
      // ensure hovered image is loaded immediately
      this._loadImageForIndex(i)
      // start description cycle
      this._startDescriptionCycle(i)
      // particle burst + pop scale
      this._createParticleBurst(i)
    },

    onProjectLeave(i) {
      // smooth resume
      this.targetSpinRate = this.spinRate
      this.hoveredProjectIndex = null
      // recompute positions to restore original spacing
      this.updateProjectPositions()
      // stop description cycle
      this._stopDescriptionCycle()
    },



    initLazyLoad() {
      // fallback to eager load if IntersectionObserver isn't available
      if (typeof IntersectionObserver === 'undefined') {
        this.$nextTick(() => {
          const imgs = (this.$refs.orbit && this.$refs.orbit.querySelectorAll) ? this.$refs.orbit.querySelectorAll('img[data-src]') : []
          imgs.forEach(img => this._setImgSrc(img))
        })
        return
      }
      const io = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting || entry.intersectionRatio > 0) {
            const img = entry.target
            this._setImgSrc(img)
            io.unobserve(img)
          }
        })
      }, { root: null, rootMargin: '200px', threshold: 0.01 })
      this.lazyObserver = io
      this.$nextTick(() => {
        const imgs = (this.$refs.orbit && this.$refs.orbit.querySelectorAll) ? this.$refs.orbit.querySelectorAll('img[data-src]') : []
        imgs.forEach(img => io.observe(img))
      })
    },

    _setImgSrc(img) {
      if (!img || img.dataset._loaded) return
      const src = img.dataset.src
      if (!src) return
      img.dataset._loaded = '1'
      img.addEventListener('load', () => img.classList.add('loaded'))
      img.src = src
      img.setAttribute('loading', 'lazy')
    },

    _loadImageForIndex(i) {
      this.$nextTick(() => {
        const imgs = (this.$refs.orbit && this.$refs.orbit.querySelectorAll) ? this.$refs.orbit.querySelectorAll('img[data-src]') : []
        if (imgs[i]) this._setImgSrc(imgs[i])
      })
    },

    /* Description cycling helpers */
    _getDescriptionsFor(i) {
      const p = (this.projects && this.projects[i]) ? this.projects[i].description : null
      if (!p) return ['']
      return Array.isArray(p) ? p : [String(p)]
    },

    _computeDisplayDuration(text) {
      // Show duration based on character length, clamped to 3000–6000 ms.
      const len = Math.max(0, (text || '').length)
      const minMs = 3000
      const maxMs = 6000
      const scaleLen = 120 /* characters that map to maxMs */
      const ratio = Math.min(len, scaleLen) / scaleLen
      const dur = Math.round(minMs + ratio * (maxMs - minMs))
      return dur
    },

    _startDescriptionCycle(i) {
      // ensure any previous cycle is stopped
      this._stopDescriptionCycle()
      const arr = this._getDescriptionsFor(i)
      if (!arr || arr.length === 0) return
      this._descIdx = 0
      this.currentHoverText = arr[0]
      this.currentWordIndex = 0
      // ensure we have an up-to-date measurement of default text width
      this.updateHeroDefaultWidth()
      // pop animation on first show
      this.isTextPopping = true
      setTimeout(() => (this.isTextPopping = false), 220)

      const startWordLoop = () => {
        if (this.hoveredProjectIndex !== i) return
        const text = this.currentHoverText || ''
        const words = this.hoverWords
        if (!words || words.length === 0) {
          const dur = this._computeDisplayDuration(text)
          this._wordTimer = setTimeout(() => {
            if (this.hoveredProjectIndex !== i) return
            const next = (this._descIdx + 1) % arr.length
            this._descIdx = next
            this.currentHoverText = arr[next]
            this.currentWordIndex = 0
            this.isTextPopping = true
            setTimeout(() => (this.isTextPopping = false), 220)
            startWordLoop()
          }, dur)
          return
        }

        const dur = this._computeDisplayDuration(text)
        this.currentWordIndex = 0

        // Distribute the total duration across words proportionally to word length.
        const minWordMs = Math.max(40, Math.floor(this.minWordMs || 120))
        const lengths = words.map(w => Math.max(1, String(w).length))
        const totalLen = lengths.reduce((s, v) => s + v, 0) || words.length
        let rawTimes = lengths.map(l => (dur * l) / totalLen)
        let perWordTimes = rawTimes.map(r => Math.max(minWordMs, Math.round(r)))
        let totalAssigned = perWordTimes.reduce((s, v) => s + v, 0)

        // If enforcing minWordMs caused total to exceed dur, fall back to balanced allocation
        if (totalAssigned > dur) {
          perWordTimes = words.map(() => Math.max(minWordMs, Math.round(dur / words.length)))
          totalAssigned = perWordTimes.reduce((s, v) => s + v, 0)
        }
        // Adjust last word so sum(perWordTimes) === dur
        perWordTimes[perWordTimes.length - 1] += (dur - totalAssigned)

        const step = () => {
          if (this.hoveredProjectIndex !== i) return
          const current = this.currentWordIndex
          const wait = perWordTimes[current] || Math.max(minWordMs, Math.round(dur / words.length))
          if (this.currentWordIndex >= words.length - 1) {
            this._wordTimer = setTimeout(() => {
              if (this.hoveredProjectIndex !== i) return
              const next = (this._descIdx + 1) % arr.length
              this._descIdx = next
              this.currentHoverText = arr[next]
              this.currentWordIndex = 0
              this.isTextPopping = true
              setTimeout(() => (this.isTextPopping = false), 220)
              startWordLoop()
            }, wait)
          } else {
            this._wordTimer = setTimeout(() => {
              this.currentWordIndex++
              step()
            }, wait)
          }
        }

        // start loop with first word timing
        this._wordTimer = setTimeout(step, perWordTimes[0] || Math.max(minWordMs, Math.round(dur / words.length)))
      }

      startWordLoop()
    },

    _stopDescriptionCycle() {
      if (this._descTimer) {
        clearTimeout(this._descTimer)
        this._descTimer = null
      }
      if (this._wordTimer) {
        clearTimeout(this._wordTimer)
        this._wordTimer = null
      }
      this._descIdx = 0
      this.currentHoverText = ''
      this.currentWordIndex = 0
      this.isTextPopping = false
    },

    // compute per-character positions for an upright circular label
    computeLabelChars(text, thumbDia = 48, labelSize = 84) {
      const s = String(text || '')
      const n = Math.max(0, s.length)
      if (n === 0) return []
      // SVG circle params (coords 0..100)
      const cx = 50
      const cy = 50
      // compute desired radius (in px) so text sits outside the thumbnail + gap
      const halfThumb = (thumbDia / 2) || 24
      const desiredGap = Math.max(8, Math.round(thumbDia * 0.12))
      const rPx = halfThumb + desiredGap
      // convert px radius into SVG coord space (0..100 maps to labelSize px)
      let r = (rPx / Math.max(1, labelSize)) * 100
      // clamp to sensible range to avoid overflow
      r = Math.max(30, Math.min(48, r))
      // choose total arc to span: approx 12deg per char, max 180deg
      // spacingFactor > 1 increases spacing (1.0 = normal, 1.05 = +5%)
      const spacingFactor = (typeof this.labelSpacingFactor === 'number') ? this.labelSpacingFactor : 1.0
      const degPerChar = 12 * spacingFactor
      const totalDeg = Math.min(180, n * degPerChar)
      const totalRad = (totalDeg * Math.PI) / 180
      // base angle: -90deg (bottom), center the arc about that
      const baseAngle = -Math.PI / 2
      const start = baseAngle - totalRad / 2
      const step = n > 1 ? totalRad / (n - 1) : 0
      const arr = []
      for (let i = 0; i < n; i++) {
        // include global label rotation offset so the whole label orbits the thumbnail center
        const angle = start + step * i + (this.labelRotation || 0)
        const x = cx + Math.cos(angle) * r
        const y = cy + Math.sin(angle) * r
        // compute rotation so characters are tangent to the circle and remain upright
        let deg = (angle * 180) / Math.PI
        let rotate = deg + 90
        // if rotated more than 90 and less than 270, flip by 180deg to keep letters readable
        if (rotate > 90 && rotate < 270) rotate -= 180
        arr.push({ char: s[i], x: x, y: y, rotate })
      }
      return arr
    },


    /* Particle burst for hover pop */
    _createParticleBurst(i) {
      const orbit = this.$refs.orbit
      if (!orbit) return
      const items = orbit.querySelectorAll('.orbit-item')
      const item = items[i]
      if (!item) return
      const thumb = item.querySelector('.orbit-thumb')
      if (!thumb) return
      const rect = thumb.getBoundingClientRect()
      const particleCount = 10 + Math.floor(Math.random() * 8)
      const timeouts = []
      for (let k = 0; k < particleCount; k++) {
        const p = document.createElement('div')
        p.className = 'orbit-particle'
        // size & color
        const size = 4 + Math.random() * 10
        p.style.width = `${size}px`
        p.style.height = `${size}px`
        p.style.left = '50%'
        p.style.top = '50%'
        p.style.opacity = '1'
        p.style.transform = 'translate(-50%,-50%) scale(1)'
        thumb.appendChild(p)

        // compute random direction
        const angle = Math.random() * Math.PI * 2
        const dist = 28 + Math.random() * Math.max(40, Math.min(window.innerWidth, window.innerHeight) * 0.06)
        const dx = Math.cos(angle) * dist
        const dy = Math.sin(angle) * dist
        // random rotation and fade out
        // animate on next frame
        requestAnimationFrame(() => {
          p.style.transition = 'transform 520ms cubic-bezier(.08,.9,.32,1), opacity 500ms ease'
          p.style.transform = `translate(${dx}px, ${dy}px) scale(0.6)`
          p.style.opacity = '0'
        })
        const to = setTimeout(() => {
          if (p && p.parentNode) p.parentNode.removeChild(p)
        }, 700)
        timeouts.push(to)
      }
      // store timeouts to clear if needed
      this._particleTimeouts.push(...timeouts)
      // cleanup array after short delay
      setTimeout(() => {
        // clear stored timeouts older than 1s
        this._particleTimeouts = this._particleTimeouts.filter(t => !!t)
      }, 1200)
    },

    resizeStars() {
      const canvas = this.$refs.starCanvas
      if (!canvas) return
      const dpr = window.devicePixelRatio || 1
      const w = window.innerWidth
      const h = window.innerHeight
      canvas.style.width = w + 'px'
      canvas.style.height = h + 'px'
      canvas.width = Math.round(w * dpr)
      canvas.height = Math.round(h * dpr)
      const ctx = this.starCtx
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    },

    spawnStar(startOutside = true) {
      // Stars travel from top-right toward bottom-left across full screen
      const w = window.innerWidth
      const h = window.innerHeight
      // start outside top/right area (spread so they cross whole viewport)
      const startX = w + Math.random() * (Math.max(200, w * 0.5))
      const startY = -Math.random() * Math.max(200, h * 0.6)
      // velocity magnitude px/sec
      const speed = 80 + Math.random() * 260
      // direction roughly down-left (135deg) with small variance
      const angle = (3 * Math.PI) / 4 + (Math.random() - 0.5) * 0.5
      const vx = Math.cos(angle) * speed // negative -> moves left
      const vy = Math.sin(angle) * speed // positive -> moves down
      return {
        x: startOutside ? startX : (w + Math.random() * 200),
        y: startOutside ? startY : (Math.random() * h * 0.6 - 100),
        vx,
        vy,
        // store original velocity so we can return to it after avoidance
        origVx: vx,
        origVy: vy,
        avoiding: false,
        avoidSide: Math.random() < 0.5 ? 1 : -1,
        // start as 1px white
        size: 1,
        alpha: 1
      }
    },

    updateStars(ts) {
      const ctx = this.starCtx
      if (!ctx) return
      const t = (ts || performance.now()) / 1000
      const dt = Math.min(0.05, Math.max(0.001, t - (this._lastStarT || t)))
      this._lastStarT = t
      const w = window.innerWidth
      const h = window.innerHeight
      // clear
      ctx.clearRect(0, 0, w, h)

      // get hero center for avoidance
      let heroCx = -9999, heroCy = -9999
      const heroEl = this.$refs.heroCopy
      if (heroEl) {
        const hr = heroEl.getBoundingClientRect()
        heroCx = hr.left + hr.width / 2
        heroCy = hr.top + hr.height / 2
      }

      const avoidRadius = Math.max(120, Math.min(w, h) * 0.18)
      const lookahead = Math.max(220, Math.max(w, h) * 0.5)

      for (let i = 0; i < this.stars.length; i++) {
        const s = this.stars[i]

        // speed and forward unit vector
        const speed = Math.hypot(s.vx, s.vy) || 1
        let nx = s.vx / speed
        let ny = s.vy / speed

        // vector from star to hero center
        const dx = heroCx - s.x
        const dy = heroCy - s.y
        
        // inter-star avoidance: detect slower stars ahead and steer away
        let interAvoidX = 0
        let interAvoidY = 0
        const interLook = 160
        const interRadius = 60
        for (let j = 0; j < this.stars.length; j++) {
          if (j === i) continue
          const s2 = this.stars[j]
          const dx2 = s2.x - s.x
          const dy2 = s2.y - s.y
          const proj2 = dx2 * nx + dy2 * ny
          const lateral2 = dx2 * -ny + dy2 * nx
          if (proj2 > 0 && proj2 < interLook && Math.abs(lateral2) < interRadius) {
            const speed1 = Math.hypot(s.vx, s.vy) || 1
            const speed2 = Math.hypot(s2.vx, s2.vy) || 1
            // only strongly avoid if the other star is slower
            if (speed2 < speed1 * 0.98) {
              // away from s2 laterally
              const side = Math.sign(lateral2) || 1
              const px = -ny * side
              const py = nx * side
              const closeness = (1 - proj2 / interLook) * (1 - Math.abs(lateral2) / interRadius)
              const speedDiff = Math.min(1.6, (speed1 - speed2) / Math.max(1, speed1))
              const influence = closeness * (0.6 + speedDiff)
              interAvoidX += px * influence
              interAvoidY += py * influence
            }
          }
        }
        // projection along forward direction (positive => ahead)
        const proj = dx * nx + dy * ny
        // lateral distance (perp) using perp dot
        const lateral = Math.abs(dx * -ny + dy * nx)

        // Start avoidance when star is heading toward hero and will pass near
        if (proj > 0 && proj < lookahead && lateral < avoidRadius) {
          s.avoiding = true
        }

        // apply inter-star avoidance by nudging velocity toward lateral direction
        if (interAvoidX !== 0 || interAvoidY !== 0) {
          // normalize interAvoid to get lateral direction
          const im = Math.hypot(interAvoidX, interAvoidY) || 1
          const px = interAvoidX / im
          const py = interAvoidY / im
          const speedCur = Math.hypot(s.vx, s.vy) || 1
          const lateralMag = speedCur * Math.min(1.4, im)
          const vxWant = nx * speedCur + px * lateralMag
          const vyWant = ny * speedCur + py * lateralMag
          s.vx += (vxWant - s.vx) * 0.36
          s.vy += (vyWant - s.vy) * 0.36
          // mark as avoiding so hero-avoid doesn't conflict too strongly
          s.avoiding = true
        }

        if (s.avoiding) {
          // compute lateral offset direction (perp vector)
          const px = -ny * s.avoidSide
          const py = nx * s.avoidSide
          // strength increases as lateral distance decreases
          const strength = (1 - Math.min(1, lateral / avoidRadius))
          // desired lateral velocity component magnitude
          const lateralMag = speed * 0.9 * strength
          // build new velocity as forward + lateral, then normalize to keep speed
          let vxNew = nx * speed + px * lateralMag
          let vyNew = ny * speed + py * lateralMag
          const mag = Math.hypot(vxNew, vyNew) || 1
          vxNew = (vxNew / mag) * speed
          vyNew = (vyNew / mag) * speed
          // apply with slight smoothing so the curve is gradual
          s.vx += (vxNew - s.vx) * 0.24
          s.vy += (vyNew - s.vy) * 0.24

          // if we've passed hero (proj negative enough) or moved sufficiently laterally, stop avoiding
          if (proj < -60 || lateral > avoidRadius * 1.2) {
            s.avoiding = false
          }
        } else {
          // ease velocity back to original course
          s.vx += (s.origVx - s.vx) * 0.06
          s.vy += (s.origVy - s.vy) * 0.06
        }

        // update position
        s.x += s.vx * dt
        s.y += s.vy * dt

        // calculate brightness based on distance from center
        const centerX = w / 2
        const centerY = h / 2
        const dist = Math.hypot(s.x - centerX, s.y - centerY)
        const maxDist = Math.hypot(centerX, centerY)
        s.alpha = 0.05 + 0.95 * Math.max(0, 1 - dist / maxDist)

        // draw a 4-step tail behind the star along its incoming direction
        // alphas: 100%, 75%, 50%, 10% with offsets roughly covering ~4-7px tail
        const tailAlphas = [1, 0.75, 0.5, 0.1]
        const tailOffsets = [0, 2.5, 4.5, 6.5]
        // forward unit vector (reuse nx, ny computed above)
        const fx = nx
        const fy = ny
        // tail is behind, so step backwards along forward vector
        for (let g = 0; g < tailAlphas.length; g++) {
          const ox = s.x - fx * tailOffsets[g]
          const oy = s.y - fy * tailOffsets[g]
          ctx.fillStyle = `rgba(255,255,255,${(s.alpha || 1) * tailAlphas[g]})`
          ctx.fillRect(Math.round(ox), Math.round(oy), 1, 1)
        }

        // respawn when fully out of view (left or bottom)
        if (s.x < -120 || s.y > h + 120) {
          this.stars[i] = this.spawnStar(true)
        }
      }
    },

    onHaloEnter(idx, ev) {
      const h = this.halos[idx]
      if (!h) return
      // vector from mouse to halo center
      const mx = ev.clientX
      const my = ev.clientY
      const dx = h.baseCenter.cx - mx
      const dy = h.baseCenter.cy - my
      const dist = Math.hypot(dx, dy) || 1
      const nx = dx / dist
      const ny = dy / dist
      // move ~20% of smallest screen dimension away, plus small randomness
      const minDim = Math.min(window.innerWidth, window.innerHeight)
      const moveBase = 0.2 * minDim
      let tx = nx * moveBase + (Math.random() - 0.5) * 30
      let ty = ny * moveBase + (Math.random() - 0.5) * 30
      // clamp new center to remain within viewport (respecting half size)
      const halfW = h.baseRect.width / 2
      const halfH = h.baseRect.height / 2
      let newCx = h.baseCenter.cx + tx
      let newCy = h.baseCenter.cy + ty
      if (newCx < halfW) tx += (halfW - newCx)
      if (newCx > window.innerWidth - halfW) tx -= (newCx - (window.innerWidth - halfW))
      if (newCy < halfH) ty += (halfH - newCy)
      if (newCy > window.innerHeight - halfH) ty -= (newCy - (window.innerHeight - halfH))
      // apply as new anchor offset (persistent)
      h.anchorX += tx
      h.anchorY += ty
      // update baseCenter to reflect new anchor
      h.baseCenter.cx += tx
      h.baseCenter.cy += ty
      // set targets so it moves there smoothly
      h.targetX = h.anchorX
      h.targetY = h.anchorY
    },
    animate(ts) {
      const ease = 0.12
      this.rotX += (this.targetX - this.rotX) * ease
      this.rotY += (this.targetY - this.rotY) * ease

      const t = (ts || performance.now()) / 1000
      const dt = Math.min(0.05, Math.max(0.001, t - (this._lastOrbitT || t)))
      this._lastOrbitT = t
      // advance label rotation so per-label text orbits around the thumbnail center
      this.labelRotation = (this.labelRotation + (this.labelSpinRate || 0) * dt) % (Math.PI * 2)
      // ease current spin rate toward targetSpinRate for smooth brake/accelerate
      this.currentSpinRate += (this.targetSpinRate - this.currentSpinRate) * this.spinEase
      // integrate angle using currentSpinRate
      this.spinAngle = (this.spinAngle + this.currentSpinRate * dt) % (Math.PI * 2)
      this.updateProjectPositions()
      this.halos.forEach((h) => {
        // idle drift
        const idleX = Math.sin(t * 0.6 + h.phase) * h.idleAmp
        const idleY = Math.cos(t * 0.5 + h.phase) * (h.idleAmp * 0.6)
        h.targetX = idleX
        h.targetY = idleY
        // ease toward target
        h.x += (h.targetX - h.x) * 0.08
        h.y += (h.targetY - h.y) * 0.08
        h.el.style.setProperty('--tx', `${h.x}px`)
        h.el.style.setProperty('--ty', `${h.y}px`)

        // size lifecycle
        const elapsed = t - h.cycleStartTime
        const progress = Math.min(elapsed / h.cycleDuration, 1)
        if (h.state === 'growing') {
          h.size = progress * h.targetSize
          if (progress >= 1) {
            h.state = 'shrinking'
            h.cycleStartTime = t
          }
        } else if (h.state === 'shrinking') {
          h.size = h.targetSize * (1 - progress)
          if (progress >= 1) {
            // reborn
            h.size = 0
            h.state = 'growing'
            h.cycleStartTime = t
            h.targetSize = Math.random() * 2.0
            // new random position
            const margin = 100
            const newX = margin + Math.random() * (window.innerWidth - 2 * margin)
            const newY = margin + Math.random() * (window.innerHeight - 2 * margin)
            h.baseCenter.cx = newX
            h.baseCenter.cy = newY
            h.anchorX = 0
            h.anchorY = 0
            h.x = 0
            h.y = 0
          }
        }
        h.el.style.setProperty('--scale', h.size.toFixed(3))
      })

      // update star canvas layer
      this.updateStars(ts)

      this.rafId = requestAnimationFrame((t) => this.animate(t))
    }
  }
}
</script>

<style scoped>
.hero-scene {
  position: fixed;
  inset: 0;
  min-height: 100vh;
  background-color: #010006;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.hero-scene::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at center, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 40%),
    radial-gradient(circle at top, rgba(0, 233, 255, 0.15), transparent 40%),
    radial-gradient(circle at bottom, rgba(179, 0, 255, 0.18), transparent 40%);
  mix-blend-mode: screen;
  pointer-events: none;
}

.halo-wrap {
  position: absolute;
  width: 62vw;
  height: 150vh;
  top: -40vh;
  /* per-halo translation applied here via CSS variables */
  transform: translate3d(var(--tx, 0px), var(--ty, 0px), 0) scale(var(--scale, 0));
  will-change: transform;
}

.halo {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle, rgba(15, 80, 200, 0.32), transparent 45%);
  filter: blur(2px);
  transform: rotate(-12deg);
  mix-blend-mode: screen;
  animation: haloColor 60s ease-in-out infinite;
}

.halo-wrap.halo-right {
  right: -30vw;
  top: -10vh;
}

.halo-wrap.halo-left {
  left: -25vw;
}

.halo-right-inner {
  background: radial-gradient(circle, rgba(0, 152, 255, 0.35), transparent 35%);
  transform: rotate(18deg);
  animation-delay: 2s;
}

.halo-left-inner {
  background: radial-gradient(circle, rgba(110, 0, 255, 0.4), transparent 40%);
}

.noise {
  position: absolute;
  inset: 0;
  background-image: repeating-linear-gradient(
      rgba(255, 255, 255, 0.02) 0px,
      rgba(255, 255, 255, 0.02) 1px,
      transparent 1px,
      transparent 4px
    ),
    repeating-linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.02) 0px,
      rgba(255, 255, 255, 0.02) 1px,
      transparent 1px,
      transparent 4px
    );
  opacity: 0.3;
  animation: drift 12s linear infinite;
}

.star-rain {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
}

.hero-copy {
  position: relative;
  z-index: 3;
  color: #f4f8fb;
  font-size: 1rem;
  letter-spacing: 0.6em;
  text-transform: uppercase;
  text-shadow: 0 0 12px rgba(255, 255, 255, 0.7);
  transform-style: preserve-3d;
  will-change: transform;
}

.logo {
  font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
  /* increased ~25% */
  font-size: clamp(3.5rem, 6.25vw, 5.75rem);
  line-height: 1;
  margin-bottom: 1rem;
}

.logo-main {
  color: #d8f7ff;
  text-shadow: 0 0 15px rgba(0, 233, 255, 0.4), 0 0 28px rgba(0, 134, 255, 0.2);
}

.logo-glow {
  position: absolute;
  inset: 0;
  color: rgba(162, 255, 255, 0.5);
  filter: blur(6px);
  opacity: 0.6;
  animation: pulse 3s ease-in-out infinite;
}

.hero-copy p {
  margin: 0;
  /* increased ~25% */
  font-size: 1.19rem;
  letter-spacing: 0.55em;
  text-transform: none;
}

/* Hero-copy hover text animation */
.hero-copy-text { 
  position: relative; 
  min-height: 1.25em; /* allow multiline content */
  overflow: visible;
}
.hero-copy-text p {
  margin: 0;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  transition: transform 340ms cubic-bezier(.2,.9,.2,1), opacity 260ms ease;
  opacity: 1;
  /* default: keep on a single line */
  white-space: nowrap;
  overflow-wrap: normal;
}
.hero-copy-text .is-wrap {
  white-space: normal;
  /* ensure we never wrap into an overly narrow column: use measured default width but enforce a sensible minimum and cap */
  max-width: min(60vw, max(var(--hero-default-width, 220px), 36ch));
  min-width: max(220px, calc(var(--hero-default-width, 220px) * 0.9));
  word-break: break-word;
  text-wrap: balance;
  text-align: center;
  line-height: 1.28; /* more breathing room */
  letter-spacing: 0.08em; /* slightly more spacing for readability */
  padding-left: 0.1rem;
  padding-right: 0.1rem;
}
@media (max-width: 520px) {
  .hero-copy-text .is-wrap {
    max-width: min(92vw, max(var(--hero-default-width, 220px), 32ch));
    min-width: 180px;
    letter-spacing: 0.03em;
    line-height: 1.26;
  }
}
.hero-copy-text .hero-default.is-hidden {
  transform: translate(-50%, -10px);
  opacity: 0;
}
.hero-copy-text .hero-hover {
  opacity: 0;
  transform: translate(-50%, 8px);
  animation: heroFadeIn 420ms ease forwards;
}
@keyframes heroFadeIn {
  from { opacity: 0; transform: translate(-50%, 8px); }
  to { opacity: 1; transform: translate(-50%, 0px); }
}

/* Lazy image reveal */
.lazy-img { opacity: 0; transition: opacity 320ms ease, transform 320ms ease; display: block; }
.lazy-img.loaded { opacity: 1; transform: none; }

/* Word-by-word highlight for hover text */
.hero-copy-text .hero-hover span {
  transition: color 160ms ease, text-shadow 160ms ease, opacity 160ms ease;
  color: rgba(255,255,255,0.6); /* dimmed base for non-lit words */
  display: inline-block;
  margin-right: 0.35ch;
}
.hero-copy-text .hero-hover span:last-child {
  margin-right: 0;
}
.hero-copy-text .hero-hover .is-lit {
  color: #fff;
  text-shadow: 0 0 12px rgba(255,220,90,0.98), 0 0 28px rgba(255,180,40,0.65);
  font-weight: 600;
}
/* ensure all non-lit words are darker while a word is lit */
.hero-copy-text .hero-hover span:not(.is-lit) {
  color: rgba(255,255,255,0.45);
  opacity: 0.95;
}


/* Project orbit */
.project-orbit {
  position: absolute;
  inset: 0;
  pointer-events: none; /* let individual items handle pointer events */
  z-index: 4;
}
.orbit-item {
  position: absolute;
  pointer-events: auto;
  cursor: pointer;
  transition: transform 180ms ease;
}
.orbit-thumb {
  width: clamp(48px, 6.2vw, 72px);
  height: clamp(48px, 6.2vw, 72px);
  border-radius: 999px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.06);
  box-shadow: 0 8px 18px rgba(0,0,0,0.6);
  background: #0b0b0f;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 220ms ease, box-shadow 220ms ease;
  position: relative;
  z-index: 6;
}
.orbit-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transform-origin: center center;
  will-change: transform;
}
.orbit-item:hover { --thumb-scale: 1.06; }

.orbit-item:hover .orbit-thumb {
  box-shadow: 0 18px 40px rgba(0,0,0,0.8);
} 

.orbit-thumb {
  transform: rotate(var(--r, 0)) scale(var(--thumb-scale, 1));
  transition: transform 260ms cubic-bezier(.2,.85,.28,1), box-shadow 260ms ease;
}

.orbit-item.is-active { --thumb-scale: 2; }
.orbit-item.is-active .orbit-thumb {
  animation: popBuzz 360ms cubic-bezier(.2,.9,.2,1);
  box-shadow: 0 36px 80px rgba(0,0,0,0.9);
} 

.orbit-label {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  width: 84px;
  height: 84px;
  display: block;
  z-index: 12; /* on top of thumbnail */
  transition: width 260ms ease, height 260ms ease, transform 260ms ease;
  /* fallback small outward nudge when thumbnail scales via CSS var */
  transform-origin: center center;
  /* ensure label contents can render beyond svg box */
  overflow: visible;
}

.orbit-label-svg { overflow: visible; }
.orbit-label-svg {
  width: 100%;
  height: 100%;
  display: block;
}
.orbit-label-text {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, 'Roboto Mono', 'Courier New', monospace;
  font-size: clamp(9px, 1.0vw, 12px);
  font-weight: 700;
  fill: #ffffff;
  -webkit-font-smoothing: antialiased;
  paint-order: stroke;
  stroke: rgba(0,0,0,0.65);
  stroke-width: 0.7px;
  /* letter-spacing is controlled per-character in JS (computeLabelChars); keep CSS normal to avoid confusion */
  letter-spacing: normal;
} 
.orbit-label-text textPath {
  text-anchor: middle;
  dominant-baseline: hanging; /* align baseline toward outside of circle */
  letter-spacing: 0.63px;
} 
/* per-character label (tangent circular text - monospace small) */
.orbit-label-char {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, 'Roboto Mono', 'Courier New', monospace;
  font-weight: 700;
  fill: #ffffff;
  stroke: rgba(0,0,0,0.65);
  stroke-width: 0.5px;
  font-size: clamp(9px, 1.0vw, 12px);
  text-transform: uppercase;
  /* per-character positions are absolute; use `labelSpacingFactor` in JS to control spacing */
  letter-spacing: normal;
  paint-order: stroke;
  pointer-events: none;
}    
/* ensure good visibility even on very dark backgrounds */
.orbit-label-text, .orbit-label-char { filter: drop-shadow(0 2px 6px rgba(0,0,0,0.65)); }/* adjust visual styling for small circles */
@media (min-width: 700px) {
  .orbit-label-text { font-size: 10px }
}
@media (min-width: 1200px) {
  .orbit-label-text { font-size: 12px }
}

@keyframes popBuzz {
  0% { transform: rotate(var(--r,0)) scale(1); }
  40% { transform: rotate(var(--r,0)) scale(2.3); }
  65% { transform: rotate(var(--r,0)) scale(1.95); }
  100% { transform: rotate(var(--r,0)) scale(2); }
}

/* Particle bursts */
.orbit-particle {
  position: absolute;
  left: 0;
  top: 0;
  pointer-events: none;
  border-radius: 999px;
  transform-origin: center center;
  mix-blend-mode: screen;
  background: radial-gradient(circle at 30% 30%, rgba(255,255,255,0.95), rgba(255,200,120,0.6) 40%, rgba(255,120,80,0.3) 70%);
  will-change: transform, opacity;
  z-index: 6;
}


@keyframes haloColor {
  0% {
    filter: blur(2px) hue-rotate(0deg) saturate(1.1) brightness(0.95);
  }
  33% {
    filter: blur(2px) hue-rotate(220deg) saturate(1.05) brightness(0.92);
  }
  66% {
    filter: blur(2px) hue-rotate(310deg) saturate(0.98) brightness(0.88);
  }
  100% {
    filter: blur(2px) hue-rotate(0deg) saturate(1.1) brightness(0.95);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.6;
    transform: translate3d(0, 0, 0);
  }
  50% {
    opacity: 1;
    transform: translate3d(0, -2px, 0);
  }
}

@keyframes drift {
  0% {
    transform: translate3d(0, 0, 0);
  }
  100% {
    transform: translate3d(0, -20px, 0);
  }
}
</style>